const hydrateMobileTables = (workspace) => {
    if (typeof document === "undefined") {
        return;
    }

    if (!workspace) {
        return;
    }

    workspace.querySelectorAll(".table-responsive table").forEach((table) => {
        if (
            table.classList.contains("keep-table-mobile") ||
            table.closest(".print-area, .print-sheet, .invoice-print-page, .sps-print-page, .sps-preview-paper, .ops-preview-paper")
        ) {
            return;
        }

        // Keep multi-level headers as a scrollable table instead of assigning incorrect card labels.
        if (table.tHead?.rows.length !== 1 || table.querySelector("thead [colspan], thead [rowspan], tbody [rowspan]")) return;
        table.classList.add("soma-mobile-card-table");

        const headers = Array.from(table.tHead.rows[0].cells).map((header) =>
            header.textContent.replace(/\s+/g, " ").trim()
        );

        table.querySelectorAll("tbody tr").forEach((row) => {
            Array.from(row.children).forEach((cell, index) => {
                if (!(cell instanceof HTMLElement) || cell.tagName !== "TD" || cell.hasAttribute("colspan")) {
                    return;
                }

                const hasActions = Boolean(cell.querySelector(".btn, .btn-group, button, a"));
                const label = headers[index] || (hasActions ? "Actions" : "");
                if (label && (!cell.getAttribute("data-label") || cell.dataset.somaGeneratedLabel)) {
                    cell.setAttribute("data-label", label);
                    cell.dataset.somaGeneratedLabel = "true";
                }
                if (label) cell.querySelectorAll("input, select, textarea").forEach((input) => {
                    if (input.dataset.somaGeneratedAria || (!input.labels?.length && !input.hasAttribute("aria-label") && !input.hasAttribute("aria-labelledby"))) {
                        input.setAttribute("aria-label", `${label} - ligne ${row.rowIndex}`);
                        input.dataset.somaGeneratedAria = "true";
                    }
                });
            });
        });
    });
};

const shouldSkipManagedTable = (table) => {
    if (
        table.classList.contains("keep-table-mobile") ||
        table.closest("form, .print-area, .print-sheet, .invoice-print-page, .sps-print-page, .sps-preview-paper, .ops-preview-paper") ||
        table.querySelector("input, textarea, select")
    ) {
        return true;
    }

    const wrapper = table.closest(".table-responsive");
    if (!wrapper) return true;
    const parent = wrapper.parentElement;
    return Boolean(parent?.querySelector(".app-pagination, .sps-pagination-bar, .company-mobile-pagination"));
};

const hydrateManagedTables = (workspace) => {
    if (typeof document === "undefined") return;
    if (!workspace) return;

    workspace.querySelectorAll(".table-responsive table").forEach((table) => {
        const wrapper = table.closest(".table-responsive");
        if (!wrapper || shouldSkipManagedTable(table)) return;

        const tbody = table.tBodies?.[0];
        const rows = tbody ? Array.from(tbody.rows).filter((row) => !row.querySelector("td[colspan]")) : [];
        const total = rows.length;
        if (total <= 5) {
            const controls = wrapper.previousElementSibling;
            if (controls?.classList.contains("soma-table-controlbar")) controls.remove();
            table.classList.remove("soma-managed-table");
            rows.forEach((row) => {
                row.hidden = false;
                row.style.removeProperty("display");
            });
            return;
        }

        let controls = wrapper.previousElementSibling;
        if (!controls?.classList?.contains("soma-table-controlbar")) {
            controls = document.createElement("div");
            controls.className = "soma-table-controlbar";
            controls.setAttribute("role", "navigation");
            controls.setAttribute("aria-label", "Pagination du tableau");
            controls.innerHTML = `
                <div class="soma-table-controlbar-summary"></div>
                <label class="soma-table-controlbar-size">
                    <span>Afficher</span>
                    <select aria-label="Nombre de lignes par page">
                        <option value="5">5</option>
                        <option value="10" selected>10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </label>
                <div class="soma-table-controlbar-actions">
                    <button type="button" data-soma-page="prev" aria-label="Page precedente"><i class="bx bx-chevron-left"></i></button>
                    <span class="soma-table-controlbar-page"></span>
                    <button type="button" data-soma-page="next" aria-label="Page suivante"><i class="bx bx-chevron-right"></i></button>
                </div>
            `;
            wrapper.parentElement?.insertBefore(controls, wrapper);
        }

        if (!controls.dataset.somaManagedTableBound) {
            controls.dataset.somaManagedTableBound = "true";
            controls.addEventListener("change", (event) => {
                if (event.target instanceof HTMLSelectElement) {
                    table.dataset.somaManagedPageSize = event.target.value;
                    table.dataset.somaManagedPage = "1";
                    hydrateManagedTables(workspace);
                }
            });
            controls.addEventListener("click", (event) => {
                const button = event.target.closest("button[data-soma-page]");
                if (!button) return;
                const direction = button.getAttribute("data-soma-page");
                const current = Number(table.dataset.somaManagedPage || 1);
                table.dataset.somaManagedPage = String(direction === "next" ? current + 1 : current - 1);
                hydrateManagedTables(workspace);
            });
        }

        const select = controls.querySelector("select");
        const pageSize = Number(table.dataset.somaManagedPageSize || (window.matchMedia("(max-width: 767.98px)").matches ? 5 : 10));
        const totalPages = Math.max(Math.ceil(total / pageSize), 1);
        const page = Math.min(Math.max(Number(table.dataset.somaManagedPage || 1), 1), totalPages);
        table.dataset.somaManagedPage = String(page);
        if (select && String(select.value) !== String(pageSize)) select.value = String(pageSize);

        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        rows.forEach((row, index) => {
            const visible = index >= start && index < end;
            row.hidden = !visible;
            row.style.display = visible ? "" : "none";
        });

        const summary = controls.querySelector(".soma-table-controlbar-summary");
        const pageLabel = controls.querySelector(".soma-table-controlbar-page");
        const previous = controls.querySelector("[data-soma-page='prev']");
        const next = controls.querySelector("[data-soma-page='next']");
        const summaryText = `${start + 1}-${Math.min(end, total)} sur ${total} lignes`;
        const pageText = `Page ${page}/${totalPages}`;
        // These nodes are observed by the workspace observer; unchanged text must stay untouched.
        if (summary && summary.textContent !== summaryText) summary.textContent = summaryText;
        if (pageLabel && pageLabel.textContent !== pageText) pageLabel.textContent = pageText;
        if (previous) previous.disabled = page <= 1;
        if (next) next.disabled = page >= totalPages;
        table.classList.add("soma-managed-table");
    });
};

export const hydrateResponsiveTables = (workspace) => {
    if (!workspace) return;
    hydrateMobileTables(workspace);
    hydrateManagedTables(workspace);
};

